import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useUnsavedGuard from './useUnsavedGuard';
import { supabase } from '../lib/supabaseClient';
import { getActiveCarrierId } from '../lib/workforceBff';
import { listRulebookCategories } from '../lib/rulebooksApi';
import { MAX_PHOTOS } from '../components/admin/EvidenceGallery';
import {
  INSPECTION_SLOT_KEYS,
  loadInspectionOverrides,
  normalizeCallbackSlots,
  resolveInspectionModels,
  saveInspectionOverrides,
} from '../lib/inspectionSettings';

// Hook for the «ارتش تک‌نفره» (Solo Inspector). One call, one model, one
// stage. Mirrors useFastInspectionCouncil's multi-photo lifecycle (upload →
// POST → poll → resume-on-mount) but trimmed to the single-stage shape:
//
//   * The canonical solo slot has one stage-1 status entry.
//   * stage2 and stage3 stay 'idle' forever - showStage2=false and
//     showStage3=false in the container hide both blocks.
//   * Distinct localStorage key so a fast / full / solo inspection can
//     be in-flight at the same time in the same browser without one
//     stomping another's resume slot.
//   * No stage transitions to simulate inside pollUntilDone - the lone
//     model card stays 'running' until the callback lands. Just the poll
//     loop, no setStageStatus calls inside.
//   * No stage1Results / stage2Rankings state - solo renders only the
//     final verdict, not per-model round cards.

const LEGACY_LS_KEY = 'fttx_inspection_in_progress_solo_v1';
const RESUME_MAX_AGE_MS = 30 * 60 * 1000;
const SOLO_SLOT_KEYS = INSPECTION_SLOT_KEYS.solo;
const SOLO_SLOT_KEY = SOLO_SLOT_KEYS[0];

/**
 * Drives the solo-inspector page with multi-photo support:
 *   - manages a 1-3 entry list of photo files + per-entry preview URLs
 *   - uploads each photo to Supabase Storage in parallel (RLS-scoped)
 *   - calls /api/inspection-solo with the array of paths + EXIF coords
 *   - tracks the single-stage status so the configured card lights up live
 */
export function useSoloInspection({ slots, concealModelIdentities } = {}) {
  // Capture this mount's carrier so late responses cannot change another slot.
  const [LS_KEY] = useState(() => {
    try { localStorage.removeItem(LEGACY_LS_KEY); } catch (_) {}
    const carrierId = getActiveCarrierId();
    return carrierId ? `${LEGACY_LS_KEY}:${carrierId}` : null;
  });
  const [entries, setEntries] = useState([]);          // [{file, previewUrl}]
  const [uploadProgress, setUploadProgress] = useState([]);  // per-entry 0..1
  const [categoryId, setCategoryId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [kickoffPending, setKickoffPending] = useState(false);
  useUnsavedGuard(kickoffPending, 'ارسال بازرسی');
  const [stageStatus, setStageStatus] = useState(initialStageStatus());
  const [finalVerdict, setFinalVerdict] = useState(null);
  const [runMeta, setRunMeta] = useState(null);
  const [error, setError] = useState(null);
  const [availableCategoryIds, setAvailableCategoryIds] = useState([]);
  const [categoriesStatus, setCategoriesStatus] = useState('loading');
  const [categoriesError, setCategoriesError] = useState(null);

  const [modelOverrides, setModelOverrides] = useState(
    () => loadInspectionOverrides().solo,
  );

  const setModelAt = useCallback((idx, modelId) => {
    if (idx !== 0) return;
    try {
      const latest = loadInspectionOverrides();
      const nextSolo = [...latest.solo];
      nextSolo[idx] = modelId;
      const saved = saveInspectionOverrides({ ...latest, solo: nextSolo });
      setModelOverrides(saved.solo);
    } catch (_) {}
  }, []);

  const modelResolution = useMemo(() => {
    if (!Array.isArray(slots)) {
      return { effectiveModels: [], requestModels: undefined };
    }
    try {
      return resolveInspectionModels({
        conceal: concealModelIdentities,
        slots,
        overrides: modelOverrides,
      });
    } catch (_error) {
      return { effectiveModels: [], requestModels: undefined };
    }
  }, [concealModelIdentities, modelOverrides, slots]);
  const { effectiveModels, requestModels } = modelResolution;

  // Track preview URLs we created so we can revoke on unmount/replace.
  const previewUrlsRef = useRef([]);
  const cancelTokenRef = useRef({ cancelled: false });
  const abortControllersRef = useRef([]);

  useEffect(() => {
    // Each mount generation owns preparation, kickoff and polling, including
    // React StrictMode's effect replay. Never revive it in an async continuation.
    const token = { cancelled: false, controller: new AbortController() };
    cancelTokenRef.current = token;
    return () => {
      previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      token.cancelled = true;
      token.controller.abort();
      abortControllersRef.current.forEach((c) => c.abort());
    };
  }, []);

  const categoryRequestRef = useRef(null);
  const refreshAvailableCategories = useCallback(async () => {
    categoryRequestRef.current?.abort();
    const controller = new AbortController();
    categoryRequestRef.current = controller;
    setAvailableCategoryIds([]);
    setCategoriesStatus('loading');
    setCategoriesError(null);
    try {
      const { categories } = await listRulebookCategories({ signal: controller.signal });
      if (controller.signal.aborted) return;
      const ids = (categories || []).map((row) => row.id);
      setAvailableCategoryIds(ids);
      setCategoryId((current) => ids.includes(current) ? current : '');
      setCategoriesStatus('ready');
    } catch (e) {
      if (controller.signal.aborted) return;
      // Failed category discovery closes the picker, never keeps old rules.
      setAvailableCategoryIds([]);
      setCategoryId('');
      setCategoriesError(e.message || 'دریافت دسته‌های بازرسی ناموفق بود. دوباره تلاش کنید.');
      setCategoriesStatus('error');
    }
  }, []);

  useEffect(() => {
    refreshAvailableCategories();
    const onChanged = () => refreshAvailableCategories();
    window.addEventListener('fttx:rulebooks-changed', onChanged);
    return () => {
      categoryRequestRef.current?.abort();
      window.removeEventListener('fttx:rulebooks-changed', onChanged);
    };
  }, [refreshAvailableCategories]);

  const pollUntilDone = useCallback(async (inspectionId, accessToken, token, startedAt) => {
    const POLL_INTERVAL_MS = 4000;
    const POLL_DEADLINE_MS = 15 * 60 * 1000;
    let first = true;
    while (first || Date.now() - startedAt < POLL_DEADLINE_MS) {
      first = false;
      if (token.cancelled) throw new Error('cancelled');
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (token.cancelled) throw new Error('cancelled');
      const pollRes = await fetch(`/api/inspection-solo/${encodeURIComponent(inspectionId)}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!pollRes.ok) continue;
      const snap = await pollRes.json();
      if (snap.status === 'done') return snap;
      if (snap.status === 'error') throw new Error(snap.error_message || 'سرویس بازرس با خطا متوقف شد.');
    }
    throw new Error('زمان انتظار برای پاسخ بازرس به پایان رسید.');
  }, []);

  // Resume-on-mount. Solo is simpler than strike - no elapsed-time branching
  // for stage transitions. Just set stage1[SOLO_SLOT_KEY] = 'running' and poll.
  useEffect(() => {
    let raw = null;
    try { raw = LS_KEY ? localStorage.getItem(LS_KEY) : null; } catch (_) {}
    if (!raw) return;
    let saved;
    try { saved = JSON.parse(raw); } catch { try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch {} return; }
    if (!saved?.inspectionId || !saved?.startedAt) { try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch {} return; }
    if (Date.now() - saved.startedAt > RESUME_MAX_AGE_MS) {
      try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch {}
      return;
    }
    let cancelled = false;
    let interrupted = false;
    const token = cancelTokenRef.current;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken || cancelled) return;
      setIsProcessing(true);
      setCategoryId(saved.categoryId || '');
      setError(null);
      setStageStatus(soloStageStatus('running'));
      try {
        const payload = await pollUntilDone(saved.inspectionId, accessToken, token, saved.startedAt);
        if (cancelled) return;
        normalizeCallbackSlots([payload.stage3], SOLO_SLOT_KEYS);
        setStageStatus(soloStageStatus('done'));
        setRunMeta({ carrier: payload.carrier, rulebook_version_no: payload.rulebook_version_no ?? null });
        setFinalVerdict(payload.verdict || { violations: [] });
        try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch {}
      } catch (err) {
        if (cancelled) return;
        if (err?.name === 'AbortError' || err?.message === 'cancelled') { interrupted = true; return; }
        setError(err.message || 'خطای ناشناخته در بازیابی بازرس.');
        setStageStatus((prev) => soloStageStatus(
          prev.stage1?.[SOLO_SLOT_KEY] === 'done' ? 'done' : 'error',
        ));
        try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch {}
      } finally {
        if (!cancelled && !interrupted) setIsProcessing(false);
      }
    })();
    return () => { cancelled = true; token.cancelled = true; };
  }, [pollUntilDone, LS_KEY]);

  const handleImagesAdd = useCallback((filesOrList) => {
    const filesArr = Array.from(filesOrList || []);
    const remaining = MAX_PHOTOS - entries.length;
    const acceptedFiles = filesArr.slice(0, Math.max(0, remaining));
    // Create URLs OUTSIDE the updater. In React StrictMode dev, updater
    // functions are intentionally double-invoked to catch impure updaters;
    // running URL.createObjectURL inside the updater would leak one URL per
    // add (the first invocation's URL is orphaned in the ref while the second
    // invocation's URL is what lands in state).
    const newOnes = acceptedFiles.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);
      return { file, previewUrl };
    });
    if (newOnes.length === 0) return;
    setEntries((prev) => [...prev, ...newOnes]);
    setStageStatus(initialStageStatus());
    setFinalVerdict(null);
    setRunMeta(null);
    setError(null);
  }, [entries.length]);

  const handleImageRemove = useCallback((idx) => {
    // Side effects (URL.revokeObjectURL, ref mutation, abort) hoisted out of
    // the setEntries updater. Reading entries[idx] from the closure is safe
    // because removal is a synchronous user click - entries cannot change
    // between the click and this callback running.
    const removed = entries[idx];
    if (removed) {
      URL.revokeObjectURL(removed.previewUrl);
      previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== removed.previewUrl);
    }
    const ac = abortControllersRef.current[idx];
    if (ac) ac.abort();
    abortControllersRef.current.splice(idx, 1);
    setEntries((prev) => prev.filter((_, i) => i !== idx));
    setUploadProgress((prev) => prev.filter((_, i) => i !== idx));
  }, [entries]);

  const handleImagesClear = useCallback(() => {
    previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrlsRef.current = [];
    abortControllersRef.current.forEach((c) => c.abort());
    abortControllersRef.current = [];
    setEntries([]);
    setUploadProgress([]);
    setStageStatus(initialStageStatus());
    setFinalVerdict(null);
    setRunMeta(null);
    setError(null);
  }, []);

  const handleAnalyze = useCallback(async (coordsFromValidator) => {
    if (entries.length < 1 || isProcessing) return;
    if (!categoryId) {
      setError('لطفاً ابتدا نوع بازرسی را انتخاب کنید.');
      return;
    }

    const op = cancelTokenRef.current;
    const carrierId = getActiveCarrierId();
    if (op.cancelled) return;
    setKickoffPending(true);
    setError(null);
    setIsProcessing(true);
    setFinalVerdict(null);
    setRunMeta(null);
    setStageStatus(soloStageStatus('running'));

    let interrupted = false;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (op.cancelled) throw new Error('cancelled');
      const session = sessionData?.session;
      const accessToken = session?.access_token;
      const userId = session?.user?.id;
      if (!accessToken || !userId) {
        throw new Error('نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
      }

      // Upload all photos in parallel; abort cleanly on remove/cancel.
      setUploadProgress(new Array(entries.length).fill(0));
      // NOTE: Supabase Storage upload() does not accept a `signal:` option (as of
      // supabase-js v2). Each AbortController here is a marker only - calling
      // .abort() on it lets handleImageRemove() invalidate "in-flight" UI state
      // (and prevents the upload's setUploadProgress callback from firing if the
      // caller checks the signal), but the HTTP request to storage completes
      // regardless. Orphan files in the bucket from cancelled uploads are a known,
      // accepted gap; a future janitor task would clean them up.
      abortControllersRef.current = entries.map(() => new AbortController());
      const paths = await Promise.all(entries.map(async (entry, idx) => {
        const ext = (entry.file.name.split('.').pop() || 'jpg').toLowerCase();
        const safeExt = /^(jpg|jpeg|png|webp)$/.test(ext) ? ext : 'jpg';
        const objectPath = `${userId}/${Date.now()}-${crypto.randomUUID()}.${safeExt}`;
        const { error: upErr } = await supabase.storage
          .from('inspection-photos')
          .upload(objectPath, entry.file, {
            contentType: entry.file.type || 'image/jpeg',
            upsert: false,
          });
        if (op.cancelled) throw new Error('cancelled');
        if (upErr) {
          throw new Error(`بارگذاری عکس شماره ${idx + 1} ناموفق بود: ${upErr.message}`);
        }
        setUploadProgress((prev) => {
          const next = [...prev];
          next[idx] = 1;
          return next;
        });
        return objectPath;
      }));

      if (op.cancelled) throw new Error('cancelled');
      const kickRes = await fetch('/api/inspection-solo', {
        method: 'POST',
        signal: op.controller.signal,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          carrier_id: carrierId,
          image_paths: paths,
          category_id: categoryId,
          photo_coords: coordsFromValidator || null,
          models: requestModels,
        }),
      });

      if (op.cancelled) throw new Error('cancelled');
      if (!kickRes.ok) {
        let detail = '';
        try { const j = await kickRes.json(); detail = j.error || j.detail || JSON.stringify(j); }
        catch { detail = await kickRes.text(); }
        throw new Error(`خطا از سرور (${kickRes.status}): ${String(detail).slice(0, 300)}`);
      }
      const { inspection_id: inspectionId } = await kickRes.json();
      if (op.cancelled) throw new Error('cancelled');
      if (!inspectionId) throw new Error('سرور inspection_id بازنگرداند.');

      const startedAt = Date.now();
      try {
        if (LS_KEY) localStorage.setItem(LS_KEY, JSON.stringify({ inspectionId, categoryId, startedAt }));
      } catch (_) {}

      setKickoffPending(false);
      const payload = await pollUntilDone(inspectionId, accessToken, op, startedAt);
      if (op.cancelled) throw new Error('cancelled');

      normalizeCallbackSlots([payload.stage3], SOLO_SLOT_KEYS);
      setStageStatus(soloStageStatus('done'));
      setRunMeta({ carrier: payload.carrier, rulebook_version_no: payload.rulebook_version_no ?? null });
      setFinalVerdict(payload.verdict || { violations: [] });
      try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch (_) {}
    } catch (err) {
      if (op.cancelled || err?.name === 'AbortError' || err?.message === 'cancelled') { interrupted = true; return; }
      setError(err?.message || 'خطای ناشناخته در ارتباط با بازرس');
      setStageStatus((prev) => soloStageStatus(
        prev.stage1?.[SOLO_SLOT_KEY] === 'done' ? 'done' : 'error',
      ));
      try { if (LS_KEY) localStorage.removeItem(LS_KEY); } catch (_) {}
    } finally {
      if (!interrupted) {
        setKickoffPending(false);
        setIsProcessing(false);
      }
    }
  }, [entries, isProcessing, categoryId, pollUntilDone, requestModels, LS_KEY]);

  return {
    entries,
    uploadProgress,
    categoryId,
    onCategoryChange: setCategoryId,
    onImagesAdd: handleImagesAdd,
    onImageRemove: handleImageRemove,
    onImagesClear: handleImagesClear,
    onAnalyze: handleAnalyze,
    isProcessing,
    stageStatus,
    finalVerdict,
    runMeta,
    error,
    availableCategoryIds,
    categoriesStatus,
    categoriesError,
    refreshAvailableCategories,
    slots,
    slotKeys: SOLO_SLOT_KEYS,
    effectiveModels,
    concealModelIdentities,
    modelOverrides,
    onModelChange: setModelAt,
  };
}

function initialStageStatus() {
  return soloStageStatus('idle');
}

function soloStageStatus(status) {
  return {
    stage1: { [SOLO_SLOT_KEY]: status },
    stage2: 'idle',
    stage3: 'idle',
  };
}
